import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { extractId } from '@seen/core/utils'
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  cn,
  ItemContent,
} from '@seen/ui'
import { IconChevronDown, IconChevronUp, IconX } from '@tabler/icons-react'
import { useCallback } from 'react'
import { ContextMenuMedplumLinkItem } from '#src/components/ContextMenuMedplumLinkItem.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { useRead } from '#src/hooks/useRead.ts'

interface ConditionsAddressedBlockProps {
  conditionReferenceStrings: string[]
  isLoading?: boolean
  hasEncounter?: boolean
  isEditable?: boolean
  onReorder?: (orderedRefStrings: string[]) => void
  onRemove?: (refString: string) => void
  className?: string
}

export function ConditionsAddressedBlock({
  conditionReferenceStrings,
  isLoading,
  hasEncounter,
  isEditable = true,
  onReorder,
  onRemove,
  className,
}: ConditionsAddressedBlockProps) {
  const handleMoveUp = useCallback(
    (index: number) => {
      if (index <= 0) {
        return
      }
      const next = [...conditionReferenceStrings]
      ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
      onReorder?.(next)
    },
    [conditionReferenceStrings, onReorder]
  )

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= conditionReferenceStrings.length - 1) {
        return
      }
      const next = [...conditionReferenceStrings]
      ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
      onReorder?.(next)
    },
    [conditionReferenceStrings, onReorder]
  )

  const showContent = !isLoading && hasEncounter

  return (
    <div className={cn('flex flex-col rounded-t-md border', className)}>
      <div className="flex h-[50px] items-center gap-2 rounded-t-md border-b bg-frame px-4">
        <div className="flex items-center gap-2 text-sm leading-snug font-medium">
          Diagnoses Addressed This Visit
          {showContent && (
            <span className="font-normal text-muted-foreground">
              {conditionReferenceStrings.length}
            </span>
          )}
        </div>
      </div>
      <ItemContent className="px-4 py-2">
        {isLoading ? (
          <Loader />
        ) : !hasEncounter ? (
          <div className="text-sm text-muted-foreground">
            Link an appointment to track diagnoses
          </div>
        ) : conditionReferenceStrings.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No diagnoses added yet. @-mention a condition to add it.
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {conditionReferenceStrings.map((refString, index) => (
              <ConditionRow
                key={refString}
                conditionReferenceString={refString}
                isEditable={isEditable}
                onRemove={() => onRemove?.(refString)}
                onMoveUp={index > 0 ? () => handleMoveUp(index) : undefined}
                onMoveDown={
                  index < conditionReferenceStrings.length - 1
                    ? () => handleMoveDown(index)
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </ItemContent>
    </div>
  )
}

interface ConditionRowProps {
  conditionReferenceString: string
  isEditable?: boolean
  onRemove: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
}

function ConditionRow({
  conditionReferenceString,
  isEditable = true,
  onRemove,
  onMoveUp,
  onMoveDown,
}: ConditionRowProps) {
  const conditionId = extractId(conditionReferenceString)
  const { data: condition, isLoading } = useRead(Diagnosis, conditionId)

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 p-2">
        <Skeleton className="h-4 w-48" />
      </div>
    )
  }

  if (!condition) {
    return null
  }

  const icd10Display = condition.icd10Code?.code

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="group/row flex items-center gap-3 rounded-lg p-2 hover:bg-muted/50">
          <div className="flex-1 text-sm font-medium">
            {condition.name}
            {icd10Display && (
              <span className="ml-1.5 font-normal text-muted-foreground">
                {icd10Display}
              </span>
            )}
          </div>
          {isEditable && (
            <div className="flex gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100">
              <Button
                variant="ghost"
                size="xs"
                onClick={onMoveUp}
                disabled={!onMoveUp}
              >
                <IconChevronUp className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={onMoveDown}
                disabled={!onMoveDown}
              >
                <IconChevronDown className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="xs"
                className="-mr-1"
                onClick={onRemove}
              >
                <IconX className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuMedplumLinkItem to={condition} />
      </ContextMenuContent>
    </ContextMenu>
  )
}
